import { useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  DecoratorNode,
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  LexicalEditor,
  LexicalNode,
  $getRoot
} from 'lexical';
import { FC } from 'react';

import './TaggingEditor.css';
import { TaggableEntity } from '@publishing-engine/domain/models';

const createEditorStateConfig = (text: string) =>
  JSON.stringify({
    root: {
      children: [
        {
          children: [
            {
              detail: 0,
              format: 0,
              mode: 'normal',
              style: '',
              text,
              type: 'text',
              version: 1
            }
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          type: 'paragraph',
          version: 1
        }
      ],
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1
    }
  });

class MentionNode extends DecoratorNode<LexicalNode> {
  __mention;

  static getType() {
    return 'mention';
  }

  static clone(node: any) {
    return new MentionNode(node.__mention, node.__key);
  }

  constructor(mentionData: any, key?: string) {
    super(key);
    this.__mention = mentionData;
  }

  createDOM() {
    const span = document.createElement('span');

    // @ts-ignore
    span.contentEditable = false;
    span.className = 'mention-node';
    span.textContent = `${this.__mention.trigger}${this.__mention.name}`;
    return span;
  }

  updateDOM() {
    return false;
  }

  exportJSON() {
    return {
      type: 'mention',
      mention: this.__mention,
      version: 1
    };
  }

  static importJSON(serializedNode: any) {
    return $createMentionNode(serializedNode.mention);
  }

  // @ts-ignore
  decorate() {
    return null;
  }
}

function $createMentionNode(mentionData: any) {
  return new MentionNode(mentionData);
}

type MentionsPluginProps = {
  entities: TaggableEntity[];
  hashtagEntities: TaggableEntity[];
  onContentChange: (params: {
    note: string;
    mentions: TaggableEntity[];
  }) => void;
  setQuery: (query: string, key: '@' | '#') => void;
};

const MentionsPlugin: FC<
  MentionsPluginProps & { onMount?: (editor: LexicalEditor) => void }
> = ({ entities, hashtagEntities, onContentChange, onMount, setQuery }) => {
  const [editor] = useLexicalComposerContext();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [trigger, setTrigger] = useState<'@' | '#' | null>(null);
  const [suggestions, setSuggestions] = useState<TaggableEntity[]>([]);

  useEffect(() => {
    onMount?.(editor);
  }, [editor, onMount]);

  useEffect(() => {
    return editor.registerCommand(
      // @ts-ignore
      'linebreak',
      () => {
        setShowSuggestions(false);
        setTrigger(null);
        return false;
      },
      1
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const paragraph = root.getFirstChild();
        if (!paragraph) return;

        let fullText = '';
        const mentions: TaggableEntity[] = [];

        // @ts-ignore
        paragraph.getChildren().forEach(node => {
          if (node.getType() === 'text') {
            fullText += node.getTextContent();
          } else if (node.getType() === 'mention') {
            const mentionData = node.__mention;
            fullText += ` ${mentionData.trigger}${mentionData.name}`;

            const baseMentions = {
              id: mentionData.id,
              name: mentionData.name,
              email: mentionData.email,
              type: mentionData.type,

              ...(mentionData.trigger === '#' && {
                shortname: mentionData.shortname,
                focusIssues: mentionData.focusIssues,
                description: mentionData.description,
                organizationId: mentionData.organizationId,
                introduction: mentionData.introduction
              })
            };

            mentions.push(baseMentions);
          }
        });

        onContentChange({
          note: fullText,
          mentions
        });
      });
    });
  }, [editor, onContentChange]);

  useEffect(() => {
    setSuggestions(trigger === '@' ? entities : hashtagEntities);
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setShowSuggestions(false);
          return;
        }

        const node = selection.anchor.getNode();
        if (!node) {
          setShowSuggestions(false);
          return;
        }

        if (!trigger) {
          const textContent = node.getTextContent();
          const cursorOffset = selection.anchor.offset;
          const textBeforeCursor = textContent.slice(0, cursorOffset);
          const lastAtIndex = textBeforeCursor.lastIndexOf('@');
          const lastHashIndex = textBeforeCursor.lastIndexOf('#');

          if (lastAtIndex === -1 && lastHashIndex === -1) {
            setShowSuggestions(false);
            return;
          }

          const lastTriggerIndex = Math.max(lastAtIndex, lastHashIndex);
          const currentTrigger = textContent[lastTriggerIndex] as '@' | '#';

          // Check if cursor is right after the trigger
          if (lastTriggerIndex === cursorOffset - 1) {
            // Check if the character before the trigger is NOT the same trigger
            const charBeforeTrigger =
              lastTriggerIndex > 0 ? textContent[lastTriggerIndex - 1] : null;

            if (charBeforeTrigger !== currentTrigger) {
              setTrigger(currentTrigger);
              setShowSuggestions(true);
              setSuggestions(
                currentTrigger === '@' ? entities : hashtagEntities
              );
            }
          }
          return;
        }

        const textContent = node.getTextContent();
        const cursorOffset = selection.anchor.offset;
        const textBeforeCursor = textContent.slice(0, cursorOffset);
        const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);

        if (lastTriggerIndex === -1) {
          setShowSuggestions(false);
          setTrigger(null);
          return;
        }

        // Check if the character before the trigger is the same trigger
        const charBeforeTrigger =
          lastTriggerIndex > 0 ? textContent[lastTriggerIndex - 1] : null;

        if (charBeforeTrigger === trigger) {
          setShowSuggestions(false);
          setTrigger(null);
          return;
        }

        const query = textContent.slice(lastTriggerIndex + 1, cursorOffset);
        if (query.includes('\n')) {
          setShowSuggestions(false);
          setTrigger(null);
          return;
        }

        // Check if query starts with the trigger character
        if (query.startsWith(trigger)) {
          setShowSuggestions(false);
          setTrigger(null);
          return;
        }

        setQuery(query, trigger);
      });
    });
  }, [editor, entities, hashtagEntities, trigger]);

  const insertMention = (mention: TaggableEntity) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const node = selection.anchor.getNode();
        if (!node) return;

        const textContent = node.getTextContent();
        const cursorOffset = selection.anchor.offset;
        const lastAtIndex = textContent.lastIndexOf('@');
        const lastHashIndex = textContent.lastIndexOf('#');
        const lastTriggerIndex = Math.max(lastAtIndex, lastHashIndex);

        if (lastTriggerIndex !== -1) {
          const currentTrigger = textContent[lastTriggerIndex];

          const baseMention = {
            id: mention.id,
            name: mention.name,
            trigger: currentTrigger,
            email: mention.email,
            type: mention.type
          };

          const mentionPayload =
            currentTrigger === '#'
              ? {
                  ...baseMention,
                  shortname: mention.shortname,
                  focusIssues: mention.focusIssues,
                  description: mention.description,
                  organizationId: mention.organizationId,
                  introduction: mention.introduction
                }
              : baseMention;

          const mentionNode = $createMentionNode(mentionPayload);

          // @ts-ignore
          node.spliceText(
            lastTriggerIndex,
            cursorOffset - lastTriggerIndex,
            ''
          );

          node.insertAfter(mentionNode);
          const spaceNode = $createTextNode(' ');
          mentionNode.insertAfter(spaceNode);
          spaceNode.select();
        }
      }

      setShowSuggestions(false);
    });
  };

  return (
    <>
      {showSuggestions && (
        <Box
          backgroundColor="white"
          width="100%"
          maxHeight="180px"
          overflowY="auto"
        >
          <Flex flexDirection="column" width="100%">
            {suggestions.map(suggestion => (
              <Flex
                width="100%"
                key={suggestion.id}
                padding={4}
                justifyContent="space-between"
                textStyle="headingItem"
                borderBottom="1px solid"
                borderColor="neutralPrimaryBorder"
              >
                {trigger}
                {suggestion.name}
                <ButtonText onClick={() => insertMention(suggestion)}>
                  Select
                </ButtonText>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}
    </>
  );
};

type TaggingEditorProps = {
  entities: {
    isLoading: boolean;
    at: TaggableEntity[];
    hashtag: TaggableEntity[];
  };
  setQuery: (query: string, key: '@' | '#') => void;
  onContentChange: (params: {
    note: string;
    mentions: TaggableEntity[];
  }) => void;
  defaultValue?: string;
};

const TaggingEditor = ({
  entities,
  onContentChange,
  defaultValue = '',
  setQuery
}: TaggingEditorProps) => {
  const [editor, setEditor] = useState<LexicalEditor | null>(null);

  const initialConfig = {
    namespace: 'TaggingEditor',
    onError: () => {},
    nodes: [MentionNode],
    editorState: defaultValue
      ? createEditorStateConfig(defaultValue)
      : undefined
  };

  useEffect(() => {
    if (editor && defaultValue) {
      const state = editor.parseEditorState(
        createEditorStateConfig(defaultValue)
      );
      editor.setEditorState(state);
    }
  }, [defaultValue, editor]);

  return (
    <Box>
      <LexicalComposer initialConfig={initialConfig}>
        <Box>
          {/* @ts-ignore */}
          <div className="editor-container">
            <PlainTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={
                <div className="editor-placeholder">Write a note</div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <MentionsPlugin
            entities={entities.at}
            hashtagEntities={entities.hashtag}
            onContentChange={onContentChange}
            onMount={setEditor}
            setQuery={setQuery}
          />
        </Box>
      </LexicalComposer>
    </Box>
  );
};

export default TaggingEditor;
